import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Slider, Space, Table } from "antd";
import { useEffect, useState } from "react";
import { DIRECTIONS, VALIDATION_MESSAGE_CONFIG } from "../../../../../constants/constants";
import { VariableConfigurationModel } from "../../../../../models/scenario-frontier.model";
import { ScenarioFrontierService } from "../../../../../services/scenario-frontier.service";
import { AMOUNT_UTIL } from "../../../../../utils/amount.utils";
import i18n from "../../../../../utils/i18n";
import { AddIcon, ArrowDownIcon, DoubleArrowIcon } from "../../../../atoms/icon";
import PercentInput from "../../../../atoms/percent-input";

const VariablesConfiguration = () => {

    const [data, setData] = useState<VariableConfigurationModel[]>([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [form] = Form.useForm();
    const [editItem, setEditItem] = useState<VariableConfigurationModel>();
    const [sliderValue, setSliderValue] = useState(0);
    const [inputValue, setInputValue] = useState<0 | 100 | null>();

    const columns: any[] = [
        {
            title: i18n.t("ScenarioFrontier.lbName"),
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: i18n.t("ScenarioFrontier.lbMean"),
            dataIndex: 'mean',
            key: 'mean',
            align: "right",
            render: (p: number) => AMOUNT_UTIL.converNumberToPercentage(p, 3)
        },
        {
            title: i18n.t("ScenarioFrontier.lbStdDev"),
            dataIndex: 'std_dev',
            key: 'std_dev',
            align: "right",
            render: (p: number) => AMOUNT_UTIL.converNumberToPercentage(p, 1)
        },
        {
            title: i18n.t("ScenarioFrontier.lbDirection"),
            dataIndex: 'direction',
            key: 'direction',
            render: (p: boolean) => <span className="flex-align-center">{DIRECTIONS.find(x => x.value === p)?.name} <ArrowDownIcon style={{marginLeft: 3}} /> </span>
        },
        {
            title: i18n.t("ScenarioFrontier.lbWeight"),
            dataIndex: 'weight',
            key: 'weight',
            align: "right",
            render: (p: number) => AMOUNT_UTIL.converNumberToPercentage(p, 1)
        },
        {
            title: i18n.t("ScenarioFrontier.lbActions"),
            dataIndex: 'actions',
            key: 'actions',
            render: (text: string, record: VariableConfigurationModel) => <>
                <Space>
                    <span className="blue-text action-text" onClick={() => { edit(record.id) }}>
                        {i18n.t("ScenarioFrontier.lbEdit")}
                    </span>
                    <span className="red-text action-text" onClick={() => { deleteItem(record.id) }}>
                        {i18n.t("ScenarioFrontier.lbDelete")}
                    </span>
                </Space>
            </>
        }
    ]

    const getData = () => {
        setDataLoading(true);
        ScenarioFrontierService.getVariables().then(res => {
            setData(res.data);
        }).finally(() => setDataLoading(false));
    }

    const edit = (id: number) => {
        const item = data.find(p => p.id === id);

        if (item) {
            const tmp = { ...item };
            tmp.direction = item?.direction.toString();
            setEditItem(tmp);
            form.setFieldsValue(tmp);
            setSliderValue(tmp?.weight || 0);
            setShowPopup(true);
        }
    }

    const addNew = () => {
        form.resetFields();
        setEditItem(undefined);
        setSliderValue(0);
        setShowPopup(true);
    }

    const save = () => {
        form.submit();
    }

    const formSubmit = async () => {
        const item = form.getFieldsValue();
        if (editItem && editItem.id) {
            await ScenarioFrontierService.updateVariable(item);
        } else {
            await ScenarioFrontierService.addVariable(item);
        }

        setShowPopup(false);
        getData();
    }

    const deleteItem = (id: number) => {

        Modal.confirm({
            content: i18n.t("ScenarioFrontier.contentDelete"),
            onCancel: () => { submitDelete(id) },
            icon: '',
            width: 304,
            okText: i18n.t("Global.lbCancel"),
            cancelText: i18n.t("ScenarioFrontier.btnDelete")
        });
    }

    const submitDelete = async (id: number) => {
        await ScenarioFrontierService.deleteVariable(id);
        getData();
    }

    const slideChange = (value: number ) => {
        setSliderValue(value);
        form.setFieldsValue({ weight: value })
    }

    const inputChange = (value: 0 | 100 | null ) => {
        setInputValue(value);
        form.setFieldsValue({ weight: inputValue })
    }

    useEffect(() => {
        getData();
    }, [])

    return (
        <div className="variables-configuration">
            <h4>
                {i18n.t("ScenarioFrontier.lbVariables")}
            </h4>

            <p className="sub-title">
                <DoubleArrowIcon />
                {i18n.t("ScenarioFrontier.lbVariables")}
            </p>

            <Row>
                <Col span={24}>
                    <Table
                        className="second-table"
                        bordered
                        pagination={false}
                        loading={dataLoading}
                        dataSource={data}
                        columns={columns} rowKey="id" />
                </Col>
            </Row>

            <Row>
                <Col span={24}>
                    <Button className="add-more" type="default" shape="round" onClick={addNew}>
                        <AddIcon className="svg-icon" /> {i18n.t("ScenarioFrontier.btnAddmore")}
                    </Button>
                </Col>
            </Row>

            <Modal
                width={600}
                className="frontier-add-modal"
                title={editItem && editItem.id ? i18n.t("ScenarioFrontier.ttEditVariable") : i18n.t("ScenarioFrontier.ttNewVariable")}
                visible={showPopup}
                onCancel={() => { setShowPopup(false) }}
                footer={<Button htmlType="submit" type="primary" shape="round" onClick={save}>
                    {editItem && editItem.id ? i18n.t("ScenarioFrontier.btnSave") : i18n.t("ScenarioFrontier.btnSaveAdd")}
                </Button>}
            >
                <Form
                    layout="vertical"
                    className="modal-form"
                    onFinish={formSubmit}
                    form={form}
                    validateMessages={VALIDATION_MESSAGE_CONFIG}>
                    <Form.Item
                        name="id"
                        hidden>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="name"
                        label={i18n.t("ScenarioFrontier.lbName")}
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>

                    <Row>
                        <Col className="weight-col-1">
                            <Form.Item
                                name="weight"
                                label={`${i18n.t("ScenarioFrontier.lbWeight")} (%)`}
                                rules={[{ required: true }]}
                            >

                                <InputNumber className="weight-input" onChange={inputChange} min={0} max={100} />
                            </Form.Item>

                        </Col>

                        <Col className="weight-col-2">
                            <span className="text-1">
                                0
                            </span>
                            <Slider
                                min={1}
                                max={100}
                                onChange={slideChange}
                                value={sliderValue}
                                dots={false}
                                className="weight-slider"
                            />
                            <span className="text-2">
                                100
                            </span>
                        </Col>

                    </Row>

                    <Form.Item
                        name="direction"
                        label={i18n.t("ScenarioFrontier.lbDirection")}
                        rules={[{ required: true }]}
                    >
                        <Select>
                            {DIRECTIONS.map(item => <Select.Option key={item.name} value={item.value.toString()}>
                                {item.name}
                            </Select.Option>)}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="mean"
                        label={i18n.t("ScenarioFrontier.lbMean")}
                        rules={[{ required: true }]}
                    >
                        <PercentInput />
                    </Form.Item>

                    <Form.Item
                        name="std_dev"
                        label={i18n.t("ScenarioFrontier.lbStandardDeviation")}
                        rules={[{ required: true }]}
                    >
                        <PercentInput />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}

export default VariablesConfiguration;